import type {
  TwilioConversationsInboundPayload,
  InitializeApi,
  SendMessageApi,
  GetMessagesApi,
  GetParticipantApi,
  GetUserApi,
  SendUserEndedChatMessageApi,
  SendFileApi,
  SendTypingApi,
  LeaveConversationApi,
  GetConversationApi,
} from '../../shared/TwilioConversationsTypes';
import type {SendFn} from '../../entries/setupMessages';
import {ClientEventsHandler} from './ClientEventsHandler';
import {TwilioApiService} from './TwilioApiService';
import {MethodNotFoundError, TwilioError} from './errors';
import {Serializer} from './Serializer';

/**
 * Handler for the request sent from the client, exposes `handleMethods` class
 * method as the main handler.
 */
export class RequestHandler {
  private static instance: RequestHandler;

  private service: TwilioApiService;

  private clientEventsHandler: ClientEventsHandler;

  constructor(send: SendFn<'TwilioConversations'>) {
    this.service = new TwilioApiService();
    this.clientEventsHandler = new ClientEventsHandler(send);
  }

  static getInstance(send: SendFn<'TwilioConversations'>): RequestHandler {
    if (!RequestHandler.instance) {
      RequestHandler.instance = new RequestHandler(send);
    }
    return RequestHandler.instance;
  }

  async handleMethods(
    payload: TwilioConversationsInboundPayload,
    respond: SendFn<'TwilioConversations'>,
  ) {
    const {method, params} = payload;

    switch (method) {
      case 'initialize':
        await this.initialize(params, respond);
        break;
      case 'shutdown':
        await this.shutdown(respond);
        break;
      case 'getMessages':
        await this.getMessages(params, respond);
        break;
      case 'getConversation':
        await this.getConversation(params, respond);
        break;
      case 'leaveConversation':
        await this.leaveConversation(params, respond);
        break;
      case 'sendMessage':
        await this.sendMessage(params, respond);
        break;
      case 'sendFile':
        await this.sendFile(params, respond);
        break;
      case 'sendTyping':
        await this.sendTyping(params, respond);
        break;
      case 'getParticipant':
        await this.getParticipant(params, respond);
        break;
      case 'getUser':
        await this.getUser(params, respond);
        break;
      case 'sendUserEndedChatMessage':
        await this.sendUserEndedChatMessage(params, respond);
        break;
      default:
        throw new MethodNotFoundError(method);
    }
  }

  private registerEventCallbacks() {
    this.clientEventsHandler.register(this.service);
  }

  private clearEventCallbacks() {
    this.clientEventsHandler.clear(this.service);
  }

  private async initialize(
    params: InitializeApi['input']['params'],
    respond: SendFn<'TwilioConversations'>,
  ) {
    await this.service.initialize(params.token);
    this.registerEventCallbacks();
    respond({method: 'initialize'});
  }

  private async shutdown(respond: SendFn<'TwilioConversations'>) {
    await this.service.shutdown();
    this.clearEventCallbacks();
    respond({method: 'shutdown'});
  }

  private async getConversation(
    params: GetConversationApi['input']['params'],
    respond: SendFn<'TwilioConversations'>,
  ) {
    const conversation = await this.service.getConversation(
      params.conversationId,
    );
    respond({
      method: 'getConversation',
      data: Serializer.fromConversation(conversation),
    });
  }

  private async leaveConversation(
    params: LeaveConversationApi['input']['params'],
    respond: SendFn<'TwilioConversations'>,
  ) {
    const conversation = await this.service.leaveConversation(
      params.conversationId,
    );
    respond({
      method: 'leaveConversation',
      data: Serializer.fromConversation(conversation),
    });
  }

  private async getMessages(
    params: GetMessagesApi['input']['params'],
    respond: SendFn<'TwilioConversations'>,
  ) {
    const {conversationId, pageSize, anchor, direction} = params;
    const messages = await this.service
      .getMessages(conversationId, pageSize, anchor, direction)
      .catch((e) => {
        throw new TwilioError(e.code, e.message);
      });
    respond({
      method: 'getMessages',
      data: {
        items: await Promise.all(
          messages.items.map((m) => Serializer.fromMessage(m)),
        ),
        hasNextPage: messages.hasNextPage,
        hasPrevPage: messages.hasPrevPage,
      },
    });
  }

  private async sendMessage(
    params: SendMessageApi['input']['params'],
    respond: SendFn<'TwilioConversations'>,
  ) {
    const {conversationId, message, nonce} = params;
    const index = await this.service
      .sendMessage(conversationId, message, nonce)
      .catch((e) => {
        throw new TwilioError(e.code, e.message);
      });
    respond({
      method: 'sendMessage',
      data: {index},
    });
  }

  private async sendFile(
    params: SendFileApi['input']['params'],
    respond: SendFn<'TwilioConversations'>,
  ) {
    const {conversationId, file, nonce} = params;
    const index = await this.service
      .sendFile(conversationId, file, nonce)
      .catch((e) => {
        throw new TwilioError(e.code, e.message);
      });
    respond({
      method: 'sendFile',
      data: {index},
    });
  }

  private async sendTyping(
    params: SendTypingApi['input']['params'],
    respond: SendFn<'TwilioConversations'>,
  ) {
    const {conversationId} = params;
    await this.service.sendTyping(conversationId).catch((e) => {
      throw new TwilioError(e.code, e.message);
    });
    respond({
      method: 'sendTyping',
    });
  }

  private async sendUserEndedChatMessage(
    params: SendUserEndedChatMessageApi['input']['params'],
    respond: SendFn<'TwilioConversations'>,
  ) {
    const {conversationId} = params;
    await this.service.sendUserEndedChatMessage(conversationId).catch((e) => {
      throw new TwilioError(e.code, e.message);
    });
    respond({
      method: 'sendUserEndedChatMessage',
    });
  }

  private async getParticipant(
    params: GetParticipantApi['input']['params'],
    respond: SendFn<'TwilioConversations'>,
  ) {
    const {conversationId, identity} = params;
    const participant = await this.service
      .getParticipant(conversationId, identity)
      .catch((e) => {
        throw new TwilioError(e.code, e.message);
      });
    respond({
      method: 'getParticipant',
      data: participant
        ? await Serializer.fromParticipant(participant)
        : undefined,
    });
  }

  private async getUser(
    params: GetUserApi['input']['params'],
    respond: SendFn<'TwilioConversations'>,
  ) {
    const {identity} = params;
    const user = await this.service.getUser(identity).catch((e) => {
      throw new TwilioError(e.code, e.message);
    });
    respond({
      method: 'getUser',
      data: Serializer.fromUser(user),
    });
  }
}
